import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "modal",
    "competitionSelect", 
    "nominationSelect", 
    "criteriasList",
    "copyButton"
  ]

  static values = {
    competitionId: Number,
    nominationId: Number,
    currentCompetitionId: Number,
    currentNominationId: Number
  }

  connect() {
    this.modal = null
    this.loadCompetitions()
  }

  openModal(event) {
    event.preventDefault()
    
    // Инициализируем модальное окно Bootstrap
    if (!this.modal) {
      this.modal = new Modal(this.modalTarget)
    }
    
    // Устанавливаем текущее соревнование по умолчанию
    if (this.hasCompetitionSelectTarget && this.currentCompetitionIdValue) {
      this.competitionSelectTarget.value = this.currentCompetitionIdValue
      this.competitionIdValue = this.currentCompetitionIdValue
      this.loadNominations()
    }
    
    this.modal.show()
  }

  closeModal() {
    if (this.modal) {
      this.modal.hide()
    }
  }

  async loadCompetitions() {
    try {
      const url = this.buildUrl('get_competitions')
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      const competitions = await response.json()
      this.populateCompetitionSelect(competitions)
    } catch (error) {
      console.error('Error loading competitions:', error)
      this.showNotification('Ошибка загрузки соревнований', 'danger')
    }
  }

  populateCompetitionSelect(competitions) {
    if (!this.hasCompetitionSelectTarget) return
    
    const select = this.competitionSelectTarget
    const currentValue = select.value
    
    // Очищаем select, кроме первой опции (placeholder)
    while (select.options.length > 1) {
      select.remove(1)
    }
    
    // Добавляем опции
    competitions.forEach(competition => {
      const option = document.createElement('option')
      option.value = competition.id
      option.textContent = competition.name
      select.appendChild(option)
    })
    
    // Восстанавливаем значение, если оно было
    if (currentValue) {
      select.value = currentValue
    }
  }

  async competitionChanged(event) {
    const competitionId = event.target.value
    
    if (!competitionId) {
      this.resetNominationSelect()
      this.resetCriteriasList()
      this.disableCopyButton()
      return
    }
    
    this.competitionIdValue = parseInt(competitionId)
    await this.loadNominations()
  }

  async loadNominations() {
    if (!this.competitionIdValue) return
    
    try {
      const url = this.buildUrl('get_nominations', { 
        source_competition_id: this.competitionIdValue 
      })
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      const nominations = await response.json()
      this.populateNominationSelect(nominations)
    } catch (error) {
      console.error('Error loading nominations:', error)
      this.showNotification('Ошибка загрузки номинаций', 'danger')
    }
  }

  populateNominationSelect(nominations) {
    if (!this.hasNominationSelectTarget) return
    
    const select = this.nominationSelectTarget
    
    // Очищаем select, кроме первой опции (placeholder)
    while (select.options.length > 1) {
      select.remove(1)
    }
    
    // Добавляем опции
    nominations.forEach(nomination => {
      const option = document.createElement('option')
      option.value = nomination.id
      option.textContent = nomination.title
      select.appendChild(option)
    })
    
    // Активируем select
    select.disabled = false
    
    this.resetCriteriasList()
    this.disableCopyButton()
  }

  resetNominationSelect() {
    if (!this.hasNominationSelectTarget) return
    
    const select = this.nominationSelectTarget
    
    // Очищаем select, кроме первой опции (placeholder)
    while (select.options.length > 1) {
      select.remove(1)
    }
    
    select.disabled = true
  }

  async nominationChanged(event) {
    const nominationId = event.target.value
    
    if (!nominationId) {
      this.resetCriteriasList()
      this.disableCopyButton()
      return
    }
    
    this.nominationIdValue = parseInt(nominationId)
    await this.loadCriterias()
  }

  async loadCriterias() {
    if (!this.competitionIdValue || !this.nominationIdValue) return
    
    try {
      this.showLoading()
      
      const url = this.buildUrl('get_criterias', { 
        source_competition_id: this.competitionIdValue,
        source_nomination_id: this.nominationIdValue
      })
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      const criterias = await response.json()
      this.displayCriterias(criterias)
      
      if (criterias.length > 0) {
        this.enableCopyButton()
      } else {
        this.disableCopyButton()
      }
    } catch (error) {
      console.error('Error loading criterias:', error)
      this.showNotification('Ошибка загрузки критериев', 'danger')
      this.resetCriteriasList()
      this.disableCopyButton()
    }
  }

  displayCriterias(criterias) {
    if (!this.hasCriteriasListTarget) return
    
    const container = this.criteriasListTarget
    
    if (criterias.length === 0) {
      container.innerHTML = `
        <div class="text-center text-muted py-3">
          <i class="fa-solid fa-list-ul fa-2x mb-2"></i>
          <p>${this.getTranslation('no_criterias')}</p>
        </div>
      `
      return
    }
    
    const html = `
      <div class="list-group">
        ${criterias.map(criteria => `
          <div class="list-group-item">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <h6 class="mb-1">${this.escapeHtml(criteria.title)}</h6>
                ${criteria.description ? `
                  <small class="text-muted">${this.escapeHtml(criteria.description)}</small>
                ` : ''}
              </div>
              <span class="badge ${criteria.ctype === 'primary' ? 'bg-warning' : 'bg-info'} ms-2">
                ${this.escapeHtml(criteria.ctype_label)}
              </span>
            </div>
            ${criteria.ctype === 'primary' && criteria.decrease ? `
              <div class="text-muted small mt-1">
                <i class="fa-solid fa-arrow-down"></i> ${this.getTranslation('decrease')}
              </div>
            ` : ''}
          </div>
        `).join('')}
      </div>
      <div class="mt-2 text-muted small">
        ${this.getTranslation('criterias_count', criterias.length)}
      </div>
    `
    
    container.innerHTML = html
  }

  showLoading() {
    if (!this.hasCriteriasListTarget) return
    
    this.criteriasListTarget.innerHTML = `
      <div class="text-center text-muted py-3">
        <div class="spinner-border spinner-border-sm mb-2" role="status">
          <span class="visually-hidden">${this.getTranslation('loading')}</span>
        </div>
        <p>${this.getTranslation('loading')}</p>
      </div>
    `
  }

  resetCriteriasList() {
    if (!this.hasCriteriasListTarget) return
    
    this.criteriasListTarget.innerHTML = ''
  }

  enableCopyButton() {
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.disabled = false
    }
  }

  disableCopyButton() {
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.disabled = true
    }
  }

  async copyCriterias(event) {
    event.preventDefault()
    
    if (!this.competitionIdValue || !this.nominationIdValue) {
      this.showNotification('Выберите соревнование и номинацию', 'warning')
      return
    }
    
    // Блокируем кнопку во время копирования
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.disabled = true
      this.copyButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Копирование...'
    }
    
    try {
      const url = this.buildUrl('copy_criterias')
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          source_competition_id: this.competitionIdValue,
          source_nomination_id: this.nominationIdValue
        })
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.showNotification(result.message, 'success')
        this.closeModal()
        
        // Перезагружаем страницу, чтобы показать новые критерии
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showNotification(result.errors.join(', '), 'danger')
        this.enableCopyButton()
        this.resetCopyButton()
      }
    } catch (error) {
      console.error('Error copying criterias:', error)
      this.showNotification('Ошибка при копировании критериев', 'danger')
      this.enableCopyButton()
      this.resetCopyButton()
    }
  }

  resetCopyButton() {
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.innerHTML = this.getTranslation('copy_button')
    }
  }

  buildUrl(action, params = {}) {
    const baseUrl = window.location.pathname
    const url = new URL(`${baseUrl}/${action}`, window.location.origin)
    
    Object.keys(params).forEach(key => {
      url.searchParams.append(key, params[key])
    })
    
    return url
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getTranslation(key, count = null) {
    const translations = {
      no_criterias: this.element.dataset.noCriterias || 'Нет критериев в выбранной номинации',
      loading: this.element.dataset.loading || 'Загрузка...',
      decrease: this.element.dataset.decrease || 'Уменьшает баллы',
      copy_button: this.element.dataset.copyButton || 'Скопировать в текущую номинацию',
      criterias_count: this.element.dataset.criteriasCount || 'Всего критериев: '
    }
    
    if (key === 'criterias_count' && count !== null) {
      return `${translations.criterias_count}${count}`
    }
    
    return translations[key] || key
  }

  showNotification(message, type = 'info') {
    const alertClass = `alert-${type}`
    const notification = document.createElement('div')
    notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`
    notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;'
    notification.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    document.body.appendChild(notification)
    
    // Автоматическое удаление через 5 секунд
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, 5000)
  }
}
